import type { PaletteColor, PaletteMode } from "@mui/material";
import type {
  RunStatus,
  RunStatusLowercase,
} from "src/queries/pipelines/pipelineRuns.types";
import type { ColorKeys } from "src/theme";

/** Run status, or a palette key used as a transformation status. */
export type StatusColorKey = RunStatus | ColorKeys;

const STATUS_RUNNING_SHEEN_ANGLE = "--status-running-sheen-angle";

/** Required for animating conic-gradient angle; @property cannot live inside a CSS class. */
if (typeof CSS !== "undefined" && "registerProperty" in CSS) {
  try {
    CSS.registerProperty({
      name: STATUS_RUNNING_SHEEN_ANGLE,
      syntax: "<angle>",
      inherits: false,
      initialValue: "0deg",
    });
  } catch {
    // already registered (HMR / duplicate import)
  }
}

/**
 * Type definition for palette that includes status colors.
 * Matches the runtime structure of MUI palette with custom extensions.
 * The runStatus keys are derived from RunStatusLowercase to maintain single source of truth.
 */
type PaletteWithStatus = {
  success: PaletteColor;
  error: PaletteColor;
  info: PaletteColor;
  warning: PaletteColor;
  neutral: PaletteColor;
  runStatus: PaletteColor & Record<RunStatusLowercase, string>;
  common: { black: string; white: string };
  text?: {
    primary: string;
    [key: string]: string;
  };
};

/**
 * Maps a run status to the appropriate MUI palette color object.
 * Provides consistent color mapping across StatusChip components and Timeline charts.
 *
 * @param status - Run status (case-insensitive, accepts both uppercase and lowercase variants)
 * @param palette - MUI theme palette object
 * @returns The appropriate PaletteColor object for the status
 *
 * @example
 * ```ts
 * const palette = theme.vars?.palette || theme.palette;
 * const colors = getStatusPalette("completed", palette);
 * // Returns palette.success
 * ```
 */
export const getStatusPalette = (
  status: StatusColorKey,
  palette: PaletteWithStatus
): PaletteColor => {
  const normalized = status.toLowerCase() as RunStatusLowercase;

  switch (normalized) {
    case "completed":
    case "success":
      return palette.success;

    case "failed":
    case "error":
      return palette.error;

    case "running":
      return palette.info;

    case "preempted":
      return palette.warning;

    // case "unknown":
    // case "completed_with_retries":
    // case "with_retries":
    // case "completed_with_errors":
    // case "failed_with_errors":
    default:
      return palette.neutral;
  }
};

/**
 * Returns backgroundColor and border styles for a run status.
 * Provides consistent styling across StatusChip components.
 *
 * @param status - Run status (case-insensitive, accepts both uppercase and lowercase variants)
 * @param palette - MUI theme palette object
 * @returns CSS properties object with backgroundColor, border, and optional CSS variables
 *
 * @example
 * ```ts
 * const palette = theme.vars?.palette || theme.palette;
 * const styles = getStatusStyles("completed", palette);
 * // Returns { "--base-color": "...", backgroundColor: "...", border: "..." }
 * ```
 */
export const getStatusStyles = (
  status: RunStatus,
  palette: PaletteWithStatus,
  mode?: PaletteMode
): Record<string, unknown> => {
  if (!status) {
    return {};
  }

  const loweredCaseStatus = status.toLowerCase() as RunStatusLowercase;

  // Get color from runStatus mapping for dotted border statuses
  const runStatusColor =
    palette.runStatus[loweredCaseStatus] || palette.neutral.light;

  switch (loweredCaseStatus) {
    case "completed":
    case "success":
      return {
        color: mode === "dark" ? palette.common.white : palette.success.dark,
        backgroundColor:
          mode === "dark" ? palette.success.contrastText : palette.success.lightest,
        borderColor: mode === "dark" ? palette.success.light : palette.success.dark,
      };

    case "completed_with_retries":
    case "with_retries":
    case "completed_with_errors":
      return {
        color: mode === "dark" ? palette.common.white : palette.success.dark,
        borderColor: mode === "dark" ? palette.success.light : palette.success.dark,
      };

    case "failed":
    case "error":
      return {
        color: mode === "dark" ? palette.common.white : palette.error.dark,
        backgroundColor:
          mode === "dark" ? palette.error.dark : palette.error.lightest,
        borderColor: mode === "dark" ? palette.error.light : palette.error.dark,
      };

    case "running": {
      const fillColor = palette.info.lightest;
      const dimBorder = mode === "dark" ? palette.info.dark : palette.info.light;
      const sheenColor = mode === "dark" ? palette.common.white : palette.info.dark;

      return {
        color: palette.info.dark,
        borderColor: "transparent",
        background: `conic-gradient(from var(${STATUS_RUNNING_SHEEN_ANGLE}), ${sheenColor} 50%, ${dimBorder}, ${sheenColor}), ${fillColor}`,
        backgroundClip: "border-area, padding-box",
        backgroundOrigin: "border-box, padding-box",
        animation: "status-running-sheen 1.2s linear infinite",
        "@keyframes status-running-sheen": {
          to: {
            [STATUS_RUNNING_SHEEN_ANGLE]: "360deg",
          },
        },
      };
    }

    case "preempted":
    case "failed_with_errors":
      return {
        color: palette.error.main,
        borderColor: runStatusColor,
      };

    case "unknown":
      return {
        color: `light-dark(var(--mui-palette-common-black), var(--mui-palette-common-white))`,
        backgroundColor: palette.runStatus.unknown,
        borderColor: palette.neutral.main,
      };
    case "disabled":
      return {
        color: `light-dark(var(--mui-palette-text-primary), var(--mui-palette-neutral-dark))`,
        backgroundColor: palette.neutral.lightest,
        borderColor: palette.runStatus.disabled,
      };
    default:
      return {};
  }
};
